import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FolderOpen, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("grc_admin_token")}` });

const SCOPES = [
  ["all", "Everyone"],
  ["mlo", "Specific officers"],
  ["bdm", "Specific partners"],
];

export const BDMShelfPanel = ({ mlos = [] }) => {
  const [folders, setFolders] = useState([]);
  const [currentMonth, setCurrentMonth] = useState("");
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/admin/bdm-shelf`, { headers: authHeader() }),
        axios.get(`${API}/admin/bdm-team`, { headers: authHeader() }),
      ]);
      setFolders(s.data?.folders || []);
      setCurrentMonth(s.data?.current_month || "");
      setTeam(t.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (folder, patch) => {
    const body = {
      bdm_visible: folder.bdm_visible,
      audience_scope: folder.audience_scope,
      audience_mlo_slugs: folder.audience_mlo_slugs,
      audience_bdm_ids: folder.audience_bdm_ids,
      drop_month: folder.drop_month,
      shelf_note: folder.shelf_note,
      ...patch,
    };
    try {
      await axios.put(`${API}/admin/bdm-shelf/${folder.id}`, body, { headers: authHeader() });
      setFolders((prev) => prev.map((f) => (f.id === folder.id ? { ...f, ...body } : f)));
      toast.success("Shelf updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the shelf.");
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex justify-center rounded-2xl bg-white border border-linen-border">
        <Loader2 className="animate-spin text-teal" size={26} />
      </div>
    );
  }

  const visibleCount = folders.filter((f) => f.bdm_visible).length;

  return (
    <div className="space-y-4" data-testid="admin-bdm-shelf-panel">
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-5">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
          <FolderOpen size={13} /> Partner shelf
        </div>
        <h3 className="mt-1 font-heading font-medium text-xl text-navy">What reaches partner dashboards</h3>
        <p className="mt-1 text-sm text-ink-500">
          Tag a library folder once and everyone it applies to sees it. Set a drop month ({currentMonth})
          and it lands under “This month”; leave it blank and it sits on their standing shelf.
          {" "}<span className="text-navy font-medium">{visibleCount} of {folders.length}</span> folders are shared today.
        </p>
      </div>

      <div className="rounded-2xl bg-white border border-linen-border shadow-soft divide-y divide-linen-border overflow-hidden">
        {folders.length === 0 ? (
          <p className="p-6 text-sm text-ink-500">
            No library folders yet. Create folders in the Library, then tag them here.
          </p>
        ) : (
          folders.map((f) => {
            const open = openId === f.id;
            return (
              <div key={f.id} data-testid={`admin-shelf-row-${f.id}`}>
                <div className="p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-navy">{f.name}</span>
                      {f.bdm_visible ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-lime/25 text-navy">
                          <Check className="w-3 h-3" /> Shared
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-ink-400">Not shared</span>
                      )}
                      {f.drop_month ? (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-teal/10 text-teal border border-teal/30">
                          {f.drop_month}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-[11px] text-ink-400 mt-0.5">
                      {f.subcategory_name || "—"} · {f.asset_count} item{f.asset_count === 1 ? "" : "s"}
                      {f.bdm_visible ? ` · ${SCOPES.find(([k]) => k === f.audience_scope)?.[1] || ""}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => save(f, { bdm_visible: !f.bdm_visible })}
                      data-testid={`admin-shelf-toggle-${f.id}`}
                      className={`text-xs px-3 py-1.5 rounded-full font-semibold ${
                        f.bdm_visible ? "bg-linen text-navy hover:bg-linen-border" : "bg-navy text-white hover:bg-[#173a5f]"
                      }`}
                    >
                      {f.bdm_visible ? "Unshare" : "Share with partners"}
                    </button>
                    <button
                      onClick={() => setOpenId(open ? null : f.id)}
                      data-testid={`admin-shelf-edit-${f.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-linen text-navy hover:bg-linen-border"
                    >
                      {open ? "Close" : "Audience"}
                    </button>
                  </div>
                </div>

                {open && (
                  <ShelfRowEditor
                    folder={f}
                    mlos={mlos}
                    team={team}
                    currentMonth={currentMonth}
                    onSave={(patch) => save(f, patch)}
                  />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

function ShelfRowEditor({ folder, mlos, team, currentMonth, onSave }) {
  const [scope, setScope] = useState(folder.audience_scope || "all");
  const [slugs, setSlugs] = useState(folder.audience_mlo_slugs || []);
  const [ids, setIds] = useState(folder.audience_bdm_ids || []);
  const [dropMonth, setDropMonth] = useState(folder.drop_month || "");
  const [note, setNote] = useState(folder.shelf_note || "");

  const toggle = (list, setList, v) =>
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  return (
    <div className="px-4 pb-5 bg-linen/40 border-t border-linen-border" data-testid={`admin-shelf-editor-${folder.id}`}>
      <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <span className="text-xs uppercase tracking-widest text-ink-500">Who sees it</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {SCOPES.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setScope(key)}
                data-testid={`admin-shelf-scope-${folder.id}-${key}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${
                  scope === key ? "bg-navy text-white border-navy" : "bg-white text-navy border-linen-border hover:border-teal"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-ink-500">
            Drop month <span className="normal-case tracking-normal text-ink-400">(blank = always available)</span>
          </span>
          <div className="mt-1 flex gap-2">
            <input
              type="month"
              value={dropMonth}
              onChange={(e) => setDropMonth(e.target.value)}
              data-testid={`admin-shelf-month-${folder.id}`}
              className="w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-white focus:border-teal focus:outline-none"
            />
            <button
              onClick={() => setDropMonth(currentMonth)}
              className="text-xs px-3 rounded-xl bg-white border border-linen-border text-navy hover:border-teal whitespace-nowrap"
            >
              This month
            </button>
          </div>
        </label>
      </div>

      {scope === "mlo" && (
        <div className="mt-4">
          <span className="text-xs uppercase tracking-widest text-ink-500">Officers</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {mlos.map((m) => (
              <button
                key={m.slug}
                onClick={() => toggle(slugs, setSlugs, m.slug)}
                data-testid={`admin-shelf-mlo-${folder.id}-${m.slug}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${
                  slugs.includes(m.slug) ? "bg-teal text-white border-teal" : "bg-white text-navy border-linen-border hover:border-teal"
                }`}
              >
                {m.name}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-ink-500">Every partner assigned to these officers sees this folder.</p>
        </div>
      )}

      {scope === "bdm" && (
        <div className="mt-4">
          <span className="text-xs uppercase tracking-widest text-ink-500">Partners</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {team.length === 0 ? (
              <span className="text-xs text-ink-500 italic">No partners on the team yet.</span>
            ) : (
              team.map((b) => (
                <button
                  key={b.id}
                  onClick={() => toggle(ids, setIds, b.id)}
                  data-testid={`admin-shelf-bdm-${folder.id}-${b.id}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${
                    ids.includes(b.id) ? "bg-teal text-white border-teal" : "bg-white text-navy border-linen-border hover:border-teal"
                  }`}
                >
                  {b.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      <label className="block mt-4">
        <span className="text-xs uppercase tracking-widest text-ink-500">
          Note on their dashboard <span className="normal-case tracking-normal text-ink-400">(optional — one line of context)</span>
        </span>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Post two of these a week — captions are in the file description."
          data-testid={`admin-shelf-note-${folder.id}`}
          className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-white focus:border-teal focus:outline-none"
        />
      </label>

      <div className="mt-4 flex justify-end">
        <button
          onClick={() =>
            onSave({
              bdm_visible: true,
              audience_scope: scope,
              audience_mlo_slugs: slugs,
              audience_bdm_ids: ids,
              drop_month: dropMonth,
              shelf_note: note,
            })
          }
          data-testid={`admin-shelf-save-${folder.id}`}
          className="text-sm font-semibold px-5 py-2 rounded-full bg-navy text-white hover:bg-[#173a5f]"
        >
          Save audience
        </button>
      </div>
    </div>
  );
}

export default BDMShelfPanel;
